import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline/index.js";

function buildPageWindow({
  currentPage,
  totalPages,
  windowSize,
}: {
  currentPage: number;
  totalPages: number;
  windowSize: number;
}): number[] {
  if (totalPages <= 0) return [];
  const size = Math.min(windowSize, totalPages);
  let start = currentPage - Math.floor(size / 2);
  if (start < 0) start = 0;
  if (start + size > totalPages) start = totalPages - size;
  return Array.from({ length: size }, (_, i) => start + i);
}

function PageButton({
  label,
  onClick,
  disabled,
  active,
  title,
}: {
  label: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
  title?: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={[
        "min-w-8 h-8 px-2 rounded-md text-sm font-medium border transition-colors flex items-center justify-center",
        active
          ? "bg-slate-800 text-white border-slate-800"
          : "bg-white text-slate-600 border-slate-300 hover:border-slate-500",
        disabled ? "opacity-40 cursor-not-allowed hover:border-slate-300" : "",
      ].join(" ")}
    >
      {label}
    </button>
  );
}

export function Pagination({
  currentPage,
  totalPages,
  onChangePage,
  windowSize = 10,
}: {
  currentPage: number;
  totalPages: number;
  onChangePage: (page: number) => void;
  windowSize?: number;
}) {
  if (totalPages <= 1) return null;

  const pages = buildPageWindow({ currentPage, totalPages, windowSize });
  const isFirst = currentPage <= 0;
  const isLast = currentPage >= totalPages - 1;

  return (
    <div className={"flex flex-wrap items-center justify-center gap-1"}>
      <PageButton
        label={<ChevronDoubleLeftIcon className={"w-4 h-4"} />}
        title={"First page"}
        onClick={() => onChangePage(0)}
        disabled={isFirst}
      />
      <PageButton
        label={<ChevronLeftIcon className={"w-4 h-4"} />}
        title={"Previous page"}
        onClick={() => onChangePage(currentPage - 1)}
        disabled={isFirst}
      />

      {pages[0] > 0 && <span className={"px-1 text-slate-400"}>…</span>}
      {pages.map((page) => (
        <PageButton
          key={page}
          label={page + 1}
          onClick={() => onChangePage(page)}
          active={page === currentPage}
        />
      ))}
      {pages[pages.length - 1] < totalPages - 1 && (
        <span className={"px-1 text-slate-400"}>…</span>
      )}

      <PageButton
        label={<ChevronRightIcon className={"w-4 h-4"} />}
        title={"Next page"}
        onClick={() => onChangePage(currentPage + 1)}
        disabled={isLast}
      />
      <PageButton
        label={<ChevronDoubleRightIcon className={"w-4 h-4"} />}
        title={"Last page"}
        onClick={() => onChangePage(totalPages - 1)}
        disabled={isLast}
      />
    </div>
  );
}
